import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Globe, Mail, Phone, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import freelancersData from "../data/freelancers.js";

export default function FreelancerDetailPage() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();

  const freelancers = Array.isArray(freelancersData) ? freelancersData : [];
  const data = freelancers.find((f) => f.slug === slug);

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center">
        <h1 className="font-heading text-4xl font-semibold text-[#202C21]">
          404
        </h1>
        <p className="mt-3 text-[#5C6B5D]">Freelancer not found.</p>
        <Link
          to="/freelancers"
          className="mt-8 inline-flex items-center gap-2 text-[#5F7C63] hover:text-[#202C21]"
          data-testid="not-found-back"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
          {t.profile.back}
        </Link>
      </div>
    );
  }

  const role = lang === "en" ? data.role_en : data.role_pt;
  const description = lang === "en" ? data.description_en : data.description_pt;
  const what = lang === "en" ? data.what_en : data.what_pt;
  const works = lang === "en" ? data.selected_work_en : data.selected_work_pt;

  const contactItems = [
    data.website && {
      icon: Globe,
      label: t.profile.website,
      value: data.website,
      href: data.website.startsWith("http")
        ? data.website
        : `https://${data.website}`,
    },
    data.email && {
      icon: Mail,
      label: t.profile.email,
      value: data.email,
      href: `mailto:${data.email}`,
    },
    data.phone && {
      icon: Phone,
      label: t.profile.phone,
      value: data.phone,
      href: `tel:${data.phone}`,
    },
  ].filter(Boolean);

  return (
    <div className="bg-[#FAFAF9]" data-testid="freelancer-detail-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-24 lg:pb-32">
        <Link
          to="/freelancers"
          className="inline-flex items-center gap-2 text-sm text-[#5C6B5D] hover:text-[#202C21] transition-colors"
          data-testid="back-to-freelancers"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
          {t.profile.back}
        </Link>

        <div className="mt-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <aside className="lg:col-span-4 lg:sticky lg:top-24">
            <div className="relative aspect-[4/5] rounded-[24px] overflow-hidden bg-[#E6EDE7] border border-[rgba(117,148,122,0.2)]">
              <img
                src={data.avatar}
                alt={data.name}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="mt-6 bg-white border border-[rgba(117,148,122,0.15)] rounded-2xl p-6">
              <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#5C6B5D]">
                {t.profile.contact}
              </h3>
              <ul className="mt-5 space-y-4">
                {contactItems.length === 0 && (
                  <li className="text-sm text-[#8A9A8B] italic">
                    {t.profile.not_available}
                  </li>
                )}
                {contactItems.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-[#E6EDE7] flex items-center justify-center shrink-0">
                        <Icon
                          className="w-4 h-4 text-[#5F7C63]"
                          strokeWidth={1.75}
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A9A8B]">
                          {item.label}
                        </p>
                        <a
                          href={item.href}
                          target={item.icon === Globe ? "_blank" : undefined}
                          rel="noreferrer"
                          className="text-sm text-[#202C21] hover:text-[#5F7C63] break-all"
                          data-testid={`contact-${item.label.toLowerCase()}`}
                        >
                          {item.value}
                        </a>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          <main className="lg:col-span-8">
            <span className="inline-block text-xs uppercase tracking-[0.2em] font-bold text-[#5C6B5D]">
              Ericeira · Portugal
            </span>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-[#202C21] font-semibold">
              {data.name}
            </h1>
            <p className="mt-3 text-lg text-[#5F7C63] font-medium">{role}</p>

            <p className="mt-8 text-base sm:text-lg text-[#5C6B5D] leading-relaxed max-w-2xl">
              {description}
            </p>
            

            <section className="mt-14">
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#202C21]">
                {t.profile.what_i_do}
              </h2>
              <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                {what.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 bg-white border border-[rgba(117,148,122,0.15)] rounded-xl p-4"
                  >
                    <CheckCircle2
                      className="w-5 h-5 text-[#5F7C63] shrink-0 mt-0.5"
                      strokeWidth={1.75}
                    />
                    <span className="text-sm text-[#202C21]">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

     
            <section className="mt-14">
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#202C21]">
                {t.profile.selected_work}
              </h2>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {works.map((work, i) => (
                  <a
                    key={i}
                    href={work.url}
                    target="_blank"
                    rel="noreferrer"
                    className="group bg-white border border-[rgba(117,148,122,0.15)] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_12px_48px_rgba(32,44,33,0.08)] transition-all duration-300"
                  >
                    <div className="aspect-[16/10] bg-[#E6EDE7] overflow-hidden">
                      <img
                        src={work.image}
                        alt={work.title}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      />
                    </div>

                    <div className="p-5">
                      <h3 className="text-lg font-semibold text-[#202C21] leading-snug">
                        {work.title}
                      </h3>
                      <p className="mt-2 text-sm text-[#5C6B5D] leading-relaxed">
                        {work.subtitle}
                      </p>
                      <span className="mt-4 inline-flex items-center text-sm font-medium text-[#5F7C63] group-hover:text-[#202C21] transition-colors">
                        View project →
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            </section>

            <section className="mt-12 bg-white border border-[rgba(117,148,122,0.15)] rounded-2xl p-6 lg:p-8">
              <div className="max-w-2xl">
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#5C6B5D]">
                  Let’s talk
                </span>
                <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#202C21]">
                  Describe your project
                </h2>
                <p className="mt-3 text-[#5C6B5D] leading-relaxed">
                  Have a project in mind or just exploring ideas? Tell me a bit about what you need and I’ll get back to you.
                </p>
              </div>

              <form className="mt-8 grid gap-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Your name"
                    className="w-full border border-[rgba(117,148,122,0.2)] bg-[#FAFAF9] rounded-xl px-4 py-3 text-sm text-[#202C21] placeholder:text-[#8A9A8B] outline-none focus:border-[#75947A]"
                  />
                  <input
                    type="email"
                    placeholder="Your email"
                    className="w-full border border-[rgba(117,148,122,0.2)] bg-[#FAFAF9] rounded-xl px-4 py-3 text-sm text-[#202C21] placeholder:text-[#8A9A8B] outline-none focus:border-[#75947A]"
                  />
                </div>

                <textarea
                  placeholder="Tell me about your project"
                  rows={5}
                  className="w-full border border-[rgba(117,148,122,0.2)] bg-[#FAFAF9] rounded-xl px-4 py-3 text-sm text-[#202C21] placeholder:text-[#8A9A8B] outline-none focus:border-[#75947A] resize-none"
                />

                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center bg-[#202C21] text-white hover:bg-[#5F7C63] rounded-full px-6 py-3 text-sm font-medium transition-colors"
                  >
                    Send message →
                  </button>

                  <span className="text-sm text-[#8A9A8B]">
                    Usually replies within 48h
                  </span>
                </div>
              </form>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}